import { Component } from '@theme/component';
import { debounce, preloadImage, prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Swiper-powered zoom dialog.
 *
 * This element reuses the existing zoom dialog markup rendered in
 * `blocks/_product-media-gallery-swiper.liquid`:
 * - `ul.dialog-zoomed-gallery > li[ref="media[]"]` becomes Swiper wrapper/slides
 * - Thumbnail buttons remain as-is; we sync aria-selected + scroll into view
 *
 * Requires `assets/swiper.js` to be loaded globally (window.Swiper).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 *
 * @extends Component<Refs>
 */
export class SwiperZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();
  /** @type {any | null} */
  #swiper = null;
  /** @type {HTMLElement | null} */
  #swiperContainer = null;
  /** @type {HTMLElement | null} */
  #wrapper = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#handleDialogNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('close', this.#handleDialogNativeClose);
    this.#destroySwiper();
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event?.preventDefault?.();

    const { dialog } = this.refs;
    dialog.showModal();

    await this.#ensureSwiper();
    this.#goTo(index, { behavior: 'instant' });
  }

  async close() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /**
   * Thumbnail click handler from Liquid `on:click="/handleThumbnailClick/{{ i }}"`
   * @param {number} index
   */
  handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.#goTo(index, { behavior });
  }

  /**
   * Thumbnail hover handler to preload hi-res image
   * @param {number} index
   */
  handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    const el = media[index];
    if (!el) return;
    this.#loadHighResolutionImage(el);
  }

  /**
   * Navigate Swiper and sync thumbnails.
   * @param {number} index
   * @param {{behavior?: ScrollBehavior}} options
   */
  #goTo(index, options = { behavior: 'smooth' }) {
    if (isNaN(index) || index < 0) return;

    // Swiper slide change (controls swipe + thumb click)
    if (this.#swiper && typeof this.#swiper.slideTo === 'function') {
      const speed = options.behavior === 'instant' ? 0 : 250;
      this.#swiper.slideTo(index, speed);
    } else {
      // Fallback: scroll the target media into view
      const target = this.refs.media[index];
      target?.scrollIntoView({ behavior: options.behavior });
    }

    this.#selectThumbnail(index, options);
  }

  /**
   * Keep aria-selected + thumbnail strip position in sync.
   * @param {number} index
   * @param {{behavior?: ScrollBehavior}} options
   */
  #selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { thumbnails, media } = this.refs;
    if (!thumbnails?.children?.length) return;
    if (isNaN(index) || index < 0 || index >= thumbnails.children.length) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    const targetThumb = thumbnails.children[index];
    if (targetThumb instanceof HTMLElement) {
      scrollIntoView(targetThumb, {
        ancestor: thumbnails,
        behavior: options.behavior ?? 'smooth',
        block: 'center',
        inline: 'center',
      });
    }

    const targetMedia = media[index];
    if (targetMedia) this.#loadHighResolutionImage(targetMedia);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #handleDialogNativeClose = () => {
    // If dialog is closed by native means, still notify listeners.
    window.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Turn the existing `ul.dialog-zoomed-gallery` into a Swiper instance.
   */
  async #ensureSwiper() {
    if (this.#swiper) return;

    // Wait until Swiper is available (loaded via `assets/swiper.js`)
    await this.#waitForSwiper();

    const dialog = this.refs.dialog;
    const gallery = dialog.querySelector('ul.dialog-zoomed-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    // Wrap the <ul> in a Swiper container element (without changing Liquid markup)
    const container = document.createElement('div');
    container.className = 'swiper-dialog-zoomed-gallery swiper';
    container.setAttribute('ref', 'swiperContainer');

    gallery.parentNode?.insertBefore(container, gallery);
    container.appendChild(gallery);

    // Swiper requires `.swiper-wrapper` and `.swiper-slide`
    gallery.classList.add('swiper-wrapper');
    gallery.classList.remove('list-unstyled');

    const slides = Array.from(gallery.children);
    for (const slide of slides) {
      if (slide instanceof HTMLElement) slide.classList.add('swiper-slide');
    }

    this.#swiperContainer = container;
    this.#wrapper = gallery;

    const component = this;
    const swiper = new window.Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      speed: 250,
      resistanceRatio: 0.5,
      threshold: 10,
      longSwipesRatio: 0.1,
      longSwipesMs: 150,
      followFinger: true,
      // Important: avoid skipping multiple slides per swipe
      // (Swiper will settle on the closest slide)
      centeredSlides: false,
      watchSlidesProgress: true,
      preventInteractionOnTransition: false,
      on: {
        /** @param {any} sw */
        init: (sw) => {
          // Swiper fires `init` during construction; don't close over `swiper`.
          const startIndex = sw?.activeIndex ?? 0;
          component.#selectThumbnail(startIndex, { behavior: 'instant' });
        },
        slideChange: debounce(
          /** @param {any} sw */ (sw) => {
          const activeIndex = sw?.activeIndex ?? 0;
          component.#selectThumbnail(activeIndex, { behavior: 'smooth' });
          },
          20
        ),
      },
    });

    this.#swiper = swiper;
  }

  #destroySwiper() {
    if (this.#swiper && typeof this.#swiper.destroy === 'function') {
      this.#swiper.destroy(true, true);
    }
    this.#swiper = null;

    // Restore DOM if we wrapped it
    if (this.#swiperContainer && this.#wrapper) {
      const parent = this.#swiperContainer.parentNode;
      if (parent) parent.insertBefore(this.#wrapper, this.#swiperContainer);
      this.#swiperContainer.remove();

      this.#wrapper.classList.remove('swiper-wrapper');
      this.#wrapper.classList.add('list-unstyled');
      Array.from(this.#wrapper.children).forEach((el) => {
        if (el instanceof HTMLElement) el.classList.remove('swiper-slide');
      });
    }

    this.#swiperContainer = null;
    this.#wrapper = null;
  }

  async #waitForSwiper() {
    if (typeof window.Swiper !== 'undefined') return;
    await new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (typeof window.Swiper !== 'undefined') return resolve(true);
        if (Date.now() - start > 5000) return resolve(true);
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  /**
   * Load a high-resolution image for a media container (same idea as `zoom-dialog.js`).
   * @param {HTMLElement} mediaContainer
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', SwiperZoomDialog);
}

